import axios from 'axios'

import { httpAuth } from 'config/apiClient'
import { AppDispatch } from 'config/redux'
import endpoints from 'constants/endpoints'
import { setCommentsCounter, setViewedCommentsIds } from 'redux/actions/comments'
import { getUrlParamsString } from 'utils'

const COMMENTS_COUNTERS_CONCURRENCY = 2

type CounterQueueItem = {
  oid: string
  dispatch: AppDispatch
  subscribers: number
  cancelled: boolean
  settleListeners: Set<VoidFunction>
  cancelSource?: ReturnType<typeof axios.CancelToken.source>
}

const queuedCounters = new Set<string>()
const inFlightCounters = new Set<string>()
const counterQueue: CounterQueueItem[] = []
const counterQueueItems = new Map<string, CounterQueueItem>()
let activeCounterRequests = 0

export const isCommentsCounterRequestPending = (oid: string) => queuedCounters.has(oid) || inFlightCounters.has(oid)

const removeQueuedItem = (item: CounterQueueItem) => {
  if (counterQueueItems.get(item.oid) !== item) return

  queuedCounters.delete(item.oid)
  counterQueueItems.delete(item.oid)
}

const processCounterQueue = () => {
  while (activeCounterRequests < COMMENTS_COUNTERS_CONCURRENCY && counterQueue.length > 0) {
    const item = counterQueue.shift()
    if (!item) continue
    if (item.cancelled || item.subscribers <= 0) {
      removeQueuedItem(item)
      continue
    }

    queuedCounters.delete(item.oid)
    inFlightCounters.add(item.oid)
    activeCounterRequests += 1

    fetchCounterQueueItem(item)
  }
}

const fetchCounterQueueItem = async (item: CounterQueueItem) => {
  item.cancelSource = axios.CancelToken.source()

  try {
    const { data } = await httpAuth.get(endpoints.commentsCounter + getUrlParamsString({ oid: item.oid }), {
      cancelToken: item.cancelSource.token,
    })

    if (item.cancelled) return

    const counterData = item.oid in data ? data[item.oid] : data
    const commentsCount = typeof counterData?.num_of_comments === 'number' ? counterData.num_of_comments : 0

    item.dispatch(setCommentsCounter({ count: commentsCount, key: item.oid }))
    item.dispatch(setViewedCommentsIds({ key: item.oid, ids: counterData?.viewed_cids || [] }))
  } catch (err) {
    if (!axios.isCancel(err)) console.error(err)
  } finally {
    inFlightCounters.delete(item.oid)
    if (counterQueueItems.get(item.oid) === item) counterQueueItems.delete(item.oid)
    activeCounterRequests = Math.max(activeCounterRequests - 1, 0)
    item.settleListeners.forEach((listener) => listener())
    item.settleListeners.clear()
    processCounterQueue()
  }
}

export const scheduleCommentsCounterFetch = (oid: string, dispatch: AppDispatch, onSettled?: VoidFunction) => {
  const currentItem = counterQueueItems.get(oid)

  if (currentItem && !currentItem.cancelled) {
    currentItem.subscribers += 1
    if (onSettled) currentItem.settleListeners.add(onSettled)
    return () => {
      if (onSettled) currentItem.settleListeners.delete(onSettled)
      currentItem.subscribers = Math.max(currentItem.subscribers - 1, 0)
      if (currentItem.subscribers === 0) {
        if (queuedCounters.has(oid)) {
          currentItem.cancelled = true
          removeQueuedItem(currentItem)
        }
      }
    }
  }

  const item: CounterQueueItem = {
    oid,
    dispatch,
    subscribers: 1,
    cancelled: false,
    settleListeners: new Set(onSettled ? [onSettled] : []),
  }

  queuedCounters.add(oid)
  counterQueueItems.set(oid, item)
  counterQueue.push(item)
  processCounterQueue()

  return () => {
    if (onSettled) item.settleListeners.delete(onSettled)
    item.subscribers = Math.max(item.subscribers - 1, 0)
    if (item.subscribers === 0) {
      if (queuedCounters.has(oid)) {
        item.cancelled = true
        removeQueuedItem(item)
      }
    }
  }
}
